/**
 * Chat API Service for AmalQ
 * Handles HTTP chat API requests (conversations, history, etc.)
 * WebSocket streaming is handled separately in useChatWebSocket hook
 * Following production-grade patterns
 */

import { apiClient } from '../../../lib/api';
import { chatEndpoints } from '../../../lib/api/config';
import type {
  ChatSendMessageRequest,
  ChatApiResponse,
  ChatConversation,
  ChatMessage,
  ChatSuggestion,
  RateLimit,
  RateLimitErrorResponse,
} from '../chat/types/chat.types';

/**
 * API Response types for chat endpoints
 */
export interface ChatConversationsResponse {
  success: boolean;
  data: {
    conversations: ChatConversation[];
    total: number;
    page: number;
    limit: number;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message?: string;
  timestamp?: string;
  rateLimit?: RateLimit | null;
}

export interface ChatConversationResponse {
  success: boolean;
  data: ChatConversation;
  message?: string;
  timestamp?: string;
}

/**
 * Extended Chat API Response with rate limit info
 */
export interface ChatApiResponseWithRateLimit {
  success: boolean;
  data?: {
    conversationId: string;
    message: ChatMessage;
    isOffTopic?: boolean; // True if the question was off-topic
    suggestions?: ChatSuggestion[]; // Array of suggestions for off-topic questions
  };
  error?: string;
  timestamp?: string;
  rateLimit?: RateLimit | null;
}

/**
 * Send a chat message (HTTP endpoint to initiate conversation)
 * POST /api/v1/chat/send
 * 
 * Note: After sending via HTTP, real-time streaming happens via WebSocket
 * 
 * @param request - Chat message request
 * @param accessToken - Optional authentication token
 * @returns Promise resolving to API response with rate limit info
 */
/**
 * Parse validation errors from backend response
 */
interface ValidationErrorResponse {
  success: false;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
  };
}

/**
 * Parse rate limit headers from HTTP response headers
 * Headers format: X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset, X-RateLimit-Burst-Remaining
 */
function parseRateLimitHeaders(headers?: Record<string, string>): RateLimit | null {
  if (!headers) return null;

  const limit = headers['x-ratelimit-limit'] || headers['X-RateLimit-Limit'];
  const remaining = headers['x-ratelimit-remaining'] || headers['X-RateLimit-Remaining'];
  const reset = headers['x-ratelimit-reset'] || headers['X-RateLimit-Reset'];
  const burstRemaining = headers['x-ratelimit-burst-remaining'] || headers['X-RateLimit-Burst-Remaining'];

  if (!limit || !remaining || !reset) {
    return null;
  }

  try {
    const resetTimestamp = parseInt(reset, 10);
    return {
      limit: parseInt(limit, 10),
      remaining: parseInt(remaining, 10),
      resetAt: new Date(resetTimestamp * 1000), // Convert Unix timestamp to Date
      burstRemaining: burstRemaining ? parseInt(burstRemaining, 10) : 0,
    };
  } catch (error) {
    console.warn('Failed to parse rate limit headers:', error);
    return null;
  }
}

/**
 * Extended Chat API Response with rate limit info
 */
export interface ChatApiResponseWithRateLimit extends ChatApiResponse {
  rateLimit?: RateLimit | null;
}

function parseValidationErrors(error: unknown): { message: string; fields?: Record<string, string[]> } {
  if (error && typeof error === 'object' && 'data' in error) {
    const errorData = (error as { data: unknown }).data;
    
    if (errorData && typeof errorData === 'object' && 'error' in errorData) {
      const validationError = errorData as ValidationErrorResponse;
      
      if (validationError.error?.fields) {
        // Extract field-specific errors
        const fieldErrors = validationError.error.fields;
        const errorMessages: string[] = [];
        
        // Build user-friendly error messages from field errors
        Object.entries(fieldErrors).forEach(([, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            errorMessages.push(messages[0]); // Take first error per field
          }
        });
        
        if (errorMessages.length > 0) {
          return {
            message: errorMessages.join(' '),
            fields: fieldErrors
          };
        }
      }
      
      if (validationError.error?.message) {
        return { message: validationError.error.message };
      }
      
      if (validationError.message) {
        return { message: validationError.message };
      }
    }
  }
  
  // Fallback: try to extract message from error object
  if (error && typeof error === 'object' && 'message' in error) {
    return { message: String(error.message) };
  }
  
  return { message: 'Failed to send message. Please try again.' };
}

export async function sendChatMessage(
  request: ChatSendMessageRequest,
  accessToken?: string
): Promise<ChatApiResponseWithRateLimit> {
  try {
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    // Clean request: don't send empty referrer, validate conversationId format if provided
    const cleanedRequest: ChatSendMessageRequest = {
      ...request,
      context: request.context ? {
        page: request.context.page,
        userAgent: request.context.userAgent,
        // Only include referrer if it's not empty
        ...(request.context.referrer && request.context.referrer.trim() ? { referrer: request.context.referrer } : {})
      } : undefined
    };
    
    // If conversationId is provided, ensure it's a valid UUID format
    if (cleanedRequest.conversationId && !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(cleanedRequest.conversationId)) {
      // Invalid UUID format - remove it and let backend create a new one
      delete cleanedRequest.conversationId;
    }

    const response = await apiClient.post<ChatApiResponse>(
      chatEndpoints.send(),
      cleanedRequest,
      { headers }
    );

    // Parse rate limit headers from response
    const rateLimit = parseRateLimitHeaders(response.headers);

    // Check if response indicates an error (structured error response from API client)
    // API client returns { success: false, data: backendErrorResponse } for structured errors
    if (!response.success && response.data) {
      // Check if this is a 429 rate limit error
      const backendError = response.data as RateLimitErrorResponse | ValidationErrorResponse;
      
      // Handle rate limit errors (429) - parse rateLimit from error response
      if ('rateLimit' in backendError && backendError.rateLimit) {
        const rateLimitFromError: RateLimit = {
          limit: backendError.rateLimit.limit,
          remaining: backendError.rateLimit.remaining,
          resetAt: new Date(backendError.rateLimit.resetAt),
          burstRemaining: backendError.rateLimit.burstRemaining,
        };
        
        // Create error with rate limit info and retryAfter
        const apiError = new Error(backendError.message || 'Rate limit exceeded') as Error & { 
          fields?: Record<string, string[]>;
          rateLimit?: RateLimit;
          retryAfter?: number;
        };
        apiError.rateLimit = rateLimitFromError;
        apiError.retryAfter = backendError.retryAfter;
        throw apiError;
      }
      
      // Handle validation errors
      if ('error' in backendError && typeof backendError.error === 'object' && backendError.error !== null && 'fields' in backendError.error) {
        const validationError = parseValidationErrors({ data: backendError });
        const apiError = new Error(validationError.message);
        (apiError as Error & { fields?: Record<string, string[]> }).fields = validationError.fields;
        throw apiError;
      }
      
      // Generic error
      const validationError = parseValidationErrors({ data: backendError });
      const apiError = new Error(validationError.message);
      throw apiError;
    }

    if (!response.data) {
      throw new Error('No data received from chat message submission');
    }

    // CRITICAL: apiClient.post() wraps the backend response
    // Backend returns: { success: true, data: { conversationId: "...", message: {...} } }
    // apiClient.post() returns: { success: true, data: { success: true, data: { conversationId: "...", message: {...} } } }
    // We need to unwrap one level to match ChatApiResponseWithRateLimit structure
    const backendResponse = response.data as ChatApiResponse;
    
    // Return unwrapped response with rate limit info
    // This ensures apiResponse.data.conversationId works correctly in useChatWidget
    return {
      success: backendResponse.success,
      data: backendResponse.data, // Unwrap: backendResponse.data contains { conversationId, message, ... }
      error: backendResponse.error,
      timestamp: backendResponse.timestamp,
      rateLimit,
    } as ChatApiResponseWithRateLimit;
  } catch (error) {
    console.error('❌ Chat API: Failed to send chat message', error);
    
    // If error has fields, it's a validation error - re-throw as-is
    if (error && typeof error === 'object' && 'fields' in error) {
      throw error;
    }
    
    // Parse validation errors from structured response
    if (error && typeof error === 'object' && 'data' in error) {
      const validationError = parseValidationErrors(error);
      const apiError = new Error(validationError.message);
      (apiError as Error & { fields?: Record<string, string[]> }).fields = validationError.fields;
      throw apiError;
    }
    
    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = String(error.message);
      
      if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      if (errorMessage.includes('timeout')) {
        throw new Error('Request timed out. Please try again.');
      }
      
      throw error;
    }
    
    throw new Error('Failed to send message. Please try again.');
  }
}

/**
 * Get user's chat conversations
 * GET /api/v1/chat/conversations
 * 
 * ⚠️ REQUIRES AUTHENTICATION - Returns 401 if not logged in
 * 
 * @param accessToken - Optional authentication token (required for authenticated users)
 * @param pagination - Optional pagination parameters
 * @param pagination.page - Page number (default: 1)
 * @param pagination.limit - Items per page (default: 20, max: 50)
 * @param pagination.includeMessages - Include messages in response (default: false)
 * @returns Promise resolving to conversations list with rate limit info
 */
export async function getChatConversations(
  accessToken?: string,
  pagination?: { 
    page?: number; 
    limit?: number;
    includeMessages?: boolean;
  }
): Promise<ChatConversationsResponse> {
  try {
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const params: Record<string, string | number | boolean> = {};
    if (pagination) {
      if (pagination.page !== undefined) params.page = pagination.page;
      if (pagination.limit !== undefined) params.limit = pagination.limit;
      if (pagination.includeMessages !== undefined) {
        params.includeMessages = pagination.includeMessages;
      }
    }

    const response = await apiClient.get<ChatConversationsResponse>(
      chatEndpoints.conversations(),
      params,
      { headers }
    );

    // Parse rate limit headers from response
    const rateLimit = parseRateLimitHeaders(response.headers);

    if (!response.data) {
      throw new Error('No data received from conversations fetch');
    }

    // Add rate limit and pagination info to response
    const result: ChatConversationsResponse = {
      ...response.data,
      rateLimit,
      // Ensure pagination object exists (backend may include it)
      pagination: response.data.pagination || (response.data.data ? {
        page: response.data.data.page,
        limit: response.data.data.limit,
        total: response.data.data.total,
        totalPages: Math.ceil(response.data.data.total / response.data.data.limit),
        hasNext: response.data.data.page * response.data.data.limit < response.data.data.total,
        hasPrev: response.data.data.page > 1,
      } : undefined),
    };

    return result;
  } catch (error) {
    console.error('❌ Chat API: Failed to fetch conversations', error);
    
    // Check for 401 Unauthorized - user not authenticated
    if (error && typeof error === 'object') {
      const errorObj = error as { code?: string; statusCode?: number; message?: string };
      
      if (errorObj.code === 'HTTP_401' || errorObj.statusCode === 401 || 
          String(errorObj.message || '').includes('401') || 
          String(errorObj.message || '').includes('Unauthorized')) {
        // Throw specific error message for 401
        throw new Error('Please log in to view your conversations.');
      }
      
      if ('message' in error) {
        const errorMessage = String(error.message);
        
        if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        
        throw error;
      }
    }
    
    throw new Error('Failed to load conversations. Please try again.');
  }
}

/**
 * Get a specific conversation with full message history
 * GET /api/v1/chat/conversations/:id
 * 
 * @param conversationId - Conversation ID to fetch
 * @param accessToken - Optional authentication token
 * @returns Promise resolving to conversation with messages
 */
export async function getChatConversation(
  conversationId: string,
  accessToken?: string
): Promise<ChatConversationResponse> {
  try {
    if (!conversationId) {
      throw new Error('Conversation ID is required');
    }

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await apiClient.get<ChatConversationResponse>(
      chatEndpoints.conversation(conversationId),
      undefined,
      { headers }
    );

    // Check for structured error response (success: false)
    if (!response.success || !response.data) {
      // Extract status code from error data if available
      const errorData = response.data as unknown as { statusCode?: number; message?: string; error?: string };
      const statusCode = errorData?.statusCode;
      
      if (statusCode === 404) {
        throw new Error('Conversation not found.');
      } else if (statusCode === 403) {
        throw new Error('You do not have access to this conversation.');
      } else if (statusCode === 401) {
        throw new Error('Please log in to access this conversation.');
      }
      
      // Try to extract message from structured error
      const errorMessage = errorData?.message || errorData?.error || 'Failed to load conversation. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data as ChatConversationResponse;
  } catch (error) {
    console.error('❌ Chat API: Failed to fetch conversation', error);
    
    // Check if error has statusCode (from ApiError)
    if (error && typeof error === 'object' && 'statusCode' in error) {
      const statusCode = (error as { statusCode: number }).statusCode;
      
      if (statusCode === 404) {
        throw new Error('Conversation not found.');
      } else if (statusCode === 403) {
        throw new Error('You do not have access to this conversation.');
      } else if (statusCode === 401) {
        throw new Error('Please log in to access this conversation.');
      }
    }
    
    // Check error message for status codes
    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = String(error.message);
      
      if (errorMessage.includes('404') || errorMessage.includes('Not found') || errorMessage.includes('not found')) {
        throw new Error('Conversation not found.');
      }
      
      if (errorMessage.includes('403') || errorMessage.includes('access') || errorMessage.includes('forbidden')) {
        throw new Error('You do not have access to this conversation.');
      }
      
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        throw new Error('Please log in to access this conversation.');
      }
      
      if (errorMessage.includes('Network') || errorMessage.includes('fetch') || errorMessage.includes('timeout')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      // Re-throw with original message if it's already user-friendly
      throw error;
    }
    
    throw new Error('Failed to load conversation. Please try again.');
  }
}

/**
 * Chat API Service Object
 * Clean interface following existing patterns
 */
export const chatApi = {
  sendMessage: sendChatMessage,
  getConversations: getChatConversations,
  getConversation: getChatConversation,
} as const;

